import React, { useState, useEffect, useMemo, useRef, useCallback, memo, useDeferredValue } from 'react';
import { Container, Row, Col, Card, Button, Table, Spinner, Alert, Badge, Image, Pagination, Form, Dropdown, Modal } from 'react-bootstrap';
import Select from 'react-select';
import { IconSearch, IconFilter, IconEye, IconRefreshCw, IconChevronUp, IconChevronDown, IconTrendingUp, IconTrendingDown, IconCheck, IconX, IconStop, IconCheckSquare } from '../components/Icons';
import { fetchNewProducts, fetchFilterOptions, setFilters, setPagination, updateProduct, confirmNewProduct, addToStopList } from '../store/slices/productsSlice';
import { fetchSimilarProducts, clearSimilarProducts } from '../store/slices/similarProductsSlice';
import { fetchStatuses, changeProductStatus } from '../store/slices/statusesSlice';
import { fetchConstants } from '../store/slices/constantsSlice';
import { fetchCertificates } from '../store/slices/certificatesSlice';
import { Product } from '../store/slices/productsSlice';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import ProductTooltip from '../components/ProductTooltip';
import ImageTooltip from '../components/ImageTooltip';
import SimilarProductsModal from '../components/SimilarProductsModal';
import ProductInfoModal from '../components/ProductInfoModal';
import ProductHistoryModal from '../components/ProductHistoryModal';
import CheckerModal from '../components/CheckerModal';
import SubjectFilterSelect from '../components/SubjectFilterSelect';
import CountryFilterSelect from '../components/CountryFilterSelect';
import SourceFilterSelect from '../components/SourceFilterSelect';
import SourceBadge from '../components/SourceBadge';
import { loadCheckerPanelState, saveCheckerPanelState } from '../utils/checkerPanelStorage';
import CatalogChatMenu from '../components/Chat/CatalogChatMenu';
import {
  hasNewFormulaConstants,
  calculateNewFormula,
  getFboWb,
  isPlaceholderPurchasePercent,
  type SppRecord,
  type FboWbRecord,
  type FboOzonData,
} from '../utils/newFormulaCalculations';
import type { Certificate } from '../store/slices/certificatesSlice';
import { api } from '../services/api';
import { getWeekRangeMondayToToday } from '../utils/dateFormat';
import { toast } from 'react-toastify';

/** Ниши из source_subjects mintятся с subjects_surrogate_id_seq. */
const SUBJECT_SURROGATE_MIN = 900000000;

function isExternalSubjectProduct(product: Product): boolean {
  const source = (product.source_code || 'wb').trim();
  const sid = Number(product.subject_id);
  if (!Number.isFinite(sid)) return false;
  return sid >= SUBJECT_SURROGATE_MIN || (source !== '' && source !== 'wb');
}

/** Совпадает с src/utils/packageDimension.js: "0" и "0.00" тоже ноль. */
function isZeroPackageDimension(value: unknown): boolean {
  const n = Number(value);
  return !Number.isFinite(n) || n === 0;
}

// Интерфейс для дополнительных данных товара
interface ProductData {
  id: string;
  noveltyArticle: string; // Теперь заполняется автоматически из product.article
  article: string; // Редактируемый артикул
  name: string; // Редактируемое название
  requiredPrice: number;
  certificate: string;
  orderQuantity: string;
  packageLength: number;
  packageWidth: number;
  packageHeight: number;
  purchasePercent: number;
  isStack: boolean;
  costPrice: string;
  totalPriceYuan: number | null;
  // Новые поля для расчетов
  calculatedCostPrice?: number;
  calculatedYuanPrice?: number;
  manualYuanPrice?: number | null; // Ручное значение цены в юанях
}

// Опции сертификатов будут загружаться из Redux состояния

// Мемоизированный компонент строки таблицы для оптимизации производительности
const ProductTableRow = memo(({
  product,
  data,
  calculatedCosts,
  isRowComplete,
  onImageMouseEnter,
  onImageMouseLeave,
  onImageClick,
  onProductNameMouseEnter,
  onProductNameMouseLeave,
  onStatusChange,
  onSimilarProductsClick,
  updateProductData,
  convertRubToYuan,
  formatSubjectName,
  formatCurrency,
  formatDate,
  certificates,
  onConfirmNewProduct,
  onAddToStopList,
  isUpdating,
  validationErrors,
  roundOrderQuantity,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDragging,
  draggedProductId,
  dragOverProductIds,
  changingFieldsBatch,
  articleError,
  onClearArticleError,
  onOpenChecker,
  onRemapSubject,
}: {
  product: Product;
  data: ProductData;
  calculatedCosts: { costPrice: number; yuanPrice: number | undefined } | null;
  isRowComplete: boolean;
  onImageMouseEnter: (imageUrl: string, alt: string, event: React.MouseEvent) => void;
  onImageMouseLeave: () => void;
  onImageClick: (product: Product, event: React.MouseEvent) => void;
  onProductNameMouseEnter: (product: Product, event: React.MouseEvent) => void;
  onProductNameMouseLeave: () => void;
  onStatusChange: (productId: string, statusId: string) => void;
  onSimilarProductsClick: (product: Product, event: React.MouseEvent) => void;
  updateProductData: (productId: string, updates: Partial<ProductData>, saveImmediately?: boolean) => void;
  convertRubToYuan: (rubAmount: number) => number | null;
  formatSubjectName: (subjectName: string) => string;
  formatCurrency: (amount: number) => string;
  formatDate: (dateString: string) => string;
  certificates: Certificate[];
  onConfirmNewProduct: (productId: string, confirmed: boolean) => void;
  onAddToStopList: (productId: string) => void;
  isUpdating: boolean;
  validationErrors?: {
    orderQuantity?: boolean;
    packageLength?: boolean;
    packageWidth?: boolean;
    packageHeight?: boolean;
    requiredPrice?: boolean;
    purchasePercent?: boolean;
  };
  roundOrderQuantity: (value: string | number, price?: number) => string;
  onDragStart: (e: React.DragEvent, productId: string, fieldType: 'packageLength' | 'packageWidth' | 'packageHeight' | 'requiredPrice' | 'certificate' | 'orderQuantity', value: number | string) => void;
  onDragOver: (e: React.DragEvent, targetProductId: string, targetFieldType?: 'packageLength' | 'packageWidth' | 'packageHeight' | 'requiredPrice' | 'certificate' | 'orderQuantity') => void;
  onDrop: (e: React.DragEvent, targetProductId: string, targetFieldType?: 'packageLength' | 'packageWidth' | 'packageHeight' | 'requiredPrice' | 'certificate' | 'orderQuantity') => void;
  onDragEnd: () => void;
  isDragging: boolean;
  draggedProductId: string | null;
  dragOverProductIds: Set<string>;
  changingFieldsBatch: Set<string>;
  articleError?: string;
  onClearArticleError?: () => void;
  onOpenChecker: (sku: string) => void;
  onRemapSubject: (product: Product) => void;
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [originalName, setOriginalName] = useState<string>('');
  // Блокировка для подтвержденных товаров или товаров в стоплисте
  const isBlocked = Boolean((product.new_product && product.target_price_cny) || product.stop_list);
  // Подсветка строки: стоплист имеет приоритет над подтверждением
  const rowClass = product.stop_list 
    ? 'table-danger' 
    : (product.new_product && product.target_price_cny ? 'table-success' : '');
  const dragOverClass = dragOverProductIds.has(product.id) ? 'drag-over-row' : '';

  return (
    <tr 
      className={`${rowClass} ${dragOverClass}`}
      onDragOver={(e) => {
        // Если drag идет по строке, передаем событие без указания типа поля
        if (isDragging) {
          onDragOver(e, product.id);
        }
      }}
      onDrop={(e) => {
        // Если drop происходит на строке, передаем событие без указания типа поля
        // (будет использован исходный тип поля из draggedFieldType)
        if (isDragging) {
          onDrop(e, product.id);
        }
      }}
    >
      <td>
        {product.thumb_url ? (
          <Image
            src={product.thumb_url}
            alt={product.name}
            width={50}
            height={50}
            className="rounded product-image-hover"
            style={{ objectFit: 'cover', cursor: 'pointer' }}
            onMouseEnter={(e) => onImageMouseEnter(product.thumb_url!, product.name, e)}
            onMouseLeave={onImageMouseLeave}
            onClick={(e) => onImageClick(product, e)}
          />
        ) : (
          <div className="bg-light rounded d-flex align-items-center justify-content-center" style={{ width: 50, height: 50 }}>
            <IconEye className="text-muted" />
          </div>
        )}
      </td>
      <td>
        <div className="d-flex align-items-center gap-1">
          {product.source_code && product.source_code !== 'wb' ? (
            <SourceBadge
              code={product.source_code}
              title={product.source_title}
              color={product.source_color}
            />
          ) : null}
          {product.url || (product as any).source_code === 'wb' || !(product as any).source_code ? (
          <a
            href={product.url || `https://www.wildberries.ru/catalog/${product.id}/detail.aspx`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary text-decoration-none"
            onClick={(e) => e.stopPropagation()}
          >
            {product.id}
          </a>
          ) : (
            <span>{product.id}</span>
          )}
          <Dropdown onClick={(e) => e.stopPropagation()}>
            <Dropdown.Toggle variant="link" size="sm" className="p-0 text-muted" title="Открыть в чекере">
              <IconCheckSquare size={12} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => onOpenChecker(product.id)}>
                Открыть тут
              </Dropdown.Item>
              <Dropdown.Item onClick={() => window.open(`/products/checker?sku=${encodeURIComponent(product.id)}`, '_blank')}>
                Открыть в новой вкладке
              </Dropdown.Item>
              <Dropdown.Item onClick={() => window.open(`/products/checker?sku=${encodeURIComponent(product.id)}`, '_blank', 'noopener,noreferrer,width=900,height=700')}>
                Открыть в новом окне
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <CatalogChatMenu productId={product.id} />
        </div>
      </td>
      <td>
        <input
          type="text"
          defaultValue={data.article || data.noveltyArticle || product.article || product.product_article || ''}
          onBlur={(e) => {
            const newArticle = e.target.value.trim();
            const persisted = String(product.article || product.product_article || '').trim();
            if (newArticle === persisted) return;
            updateProductData(product.id, { article: newArticle }, true);
          }}
          onFocus={(e) => {
            onClearArticleError?.();
            e.target.select();
          }}
          placeholder="Артикул"
          className={`form-control form-control-sm${articleError ? ' is-invalid' : ''}`}
          autoComplete="off"
          spellCheck="false"
          title={articleError || undefined}
          style={{ maxWidth: '120px', textAlign: 'center' }}
        />
      </td>
      <td style={{ minWidth: '220px' }}>
        {isEditingName ? (
          <textarea
            className="form-control form-control-sm"
            defaultValue={originalName || product.name || ''}
            onBlur={(e) => {
              const newName = e.target.value.trim();
              // Отправляем на бэкенд только если значение изменилось
              if (newName !== originalName) {
                updateProductData(product.id, { name: newName }, true);
              }
              setIsEditingName(false);
            }}
            onKeyDown={(e) => {
              // При нажатии Enter (без Shift) сохраняем и закрываем редактирование
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                const newName = e.currentTarget.value.trim();
                // Отправляем на бэкенд только если значение изменилось
                if (newName !== originalName) {
                  updateProductData(product.id, { name: newName }, true);
                }
                setIsEditingName(false);
                // Убираем фокус с textarea
                e.currentTarget.blur();
              }
            }}
            rows={3}
            spellCheck={false}
            autoFocus
            style={{ width: '100%', resize: 'vertical' }}
          />
        ) : (
        <div
          className="fw-bold product-name-hover flex-grow-1"
            style={{ cursor: 'pointer', whiteSpace: 'pre-wrap' }}
            onMouseDown={(e) => {
              e.preventDefault(); // Предотвращаем blur на других элементах
              e.stopPropagation(); // Останавливаем всплытие события
              onProductNameMouseLeave(); // Скрываем tooltip при клике
              setOriginalName(product.name || '');
              setIsEditingName(true);
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          onMouseEnter={(e) => onProductNameMouseEnter(product, e)}
          onMouseLeave={onProductNameMouseLeave}
        >
            {data.name || product.name || ''}
        </div>
        )}
      </td>
      <td>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span>{formatSubjectName(product.subject_name)}</span>
          {isExternalSubjectProduct(product) && (
            <Button
              variant="outline-secondary"
              size="sm"
              className="py-0 px-1"
              title="Заменить нишу на WB"
              onClick={(e) => {
                e.stopPropagation();
                onRemapSubject(product);
              }}
            >
              → WB
            </Button>
          )}
        </div>
      </td>
      <td
        draggable={true}
        onDragStart={(e) => {
          onDragStart(e, product.id, 'requiredPrice', data.requiredPrice || 0);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDragOver(e, product.id, 'requiredPrice');
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDrop(e, product.id, 'requiredPrice');
        }}
        onDragEnd={onDragEnd}
        style={{
          cursor: isDragging && draggedProductId === product.id ? 'grabbing' : 'grab',
          userSelect: 'none'
        }}
        className={dragOverProductIds.has(product.id) ? 'drag-over-cell' : ''}
      >
        {validationErrors?.requiredPrice && (
          <div className="text-danger small mb-1" style={{ fontSize: '0.75rem' }}>
            Обязательное поле
          </div>
        )}
        <input
          key={`requiredPrice-${product.id}-${data.requiredPrice}`}
          type="number"
          defaultValue={data.requiredPrice}
          onBlur={(e) => {
            updateProductData(product.id, { requiredPrice: parseFloat(e.target.value) || 0 }, true);
            // Очищаем ошибку при изменении
            if (validationErrors?.requiredPrice) {
              // Ошибка будет очищена через родительский компонент
            }
          }}
          onFocus={(e) => e.target.select()}
          min="0"
          step="0.01"
          className={`form-control form-control-sm ${validationErrors?.requiredPrice ? 'is-invalid' : ''}`}
          autoComplete="off"
          style={validationErrors?.requiredPrice ? { borderColor: '#dc3545' } : {}}
          onClick={(e) => e.stopPropagation()}
          disabled={isBlocked}
          onMouseDown={(e) => {
            // Не останавливаем propagation, чтобы drag работал на td
            // Только если клик именно на input, не мешаем редактированию
          }}
          onDragStart={(e) => {
            // Предотвращаем drag самого input, чтобы drag работал на td
            e.stopPropagation();
            e.preventDefault();
          }}
        />
      </td>
      <td
        draggable={true}
        onDragStart={(e) => {
          onDragStart(e, product.id, 'certificate', data.certificate || '');
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDragOver(e, product.id, 'certificate');
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDrop(e, product.id, 'certificate');
        }}
        onDragEnd={onDragEnd}
        style={{
          cursor: isDragging && draggedProductId === product.id ? 'grabbing' : 'grab',
          userSelect: 'none'
        }}
        className={dragOverProductIds.has(product.id) ? 'drag-over-cell' : ''}
      >
        <select
          key={`certificate-${product.id}-${data.certificate}`}
          value={data.certificate || ''}
          onChange={(e) => updateProductData(product.id, { certificate: e.target.value })}
          className="form-select form-select-sm"
          onClick={(e) => e.stopPropagation()}
          disabled={isBlocked}
          onMouseDown={(e) => {
            // Не останавливаем propagation, чтобы drag работал на td
          }}
          onDragStart={(e) => {
            // Предотвращаем drag самого select, чтобы drag работал на td
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <option value="">Выберите</option>
          {certificates.map((certificate: Certificate) => (
            <option key={certificate.id} value={certificate.id.toString()}>
              {certificate.name}
            </option>
          ))}
        </select>
      </td>
      <td 
        draggable={true}
        onDragStart={(e) => {
          onDragStart(e, product.id, 'orderQuantity', data.orderQuantity || '');
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDragOver(e, product.id, 'orderQuantity');
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDrop(e, product.id, 'orderQuantity');
        }}
        onDragEnd={onDragEnd}
        style={{
          position: 'relative',
          cursor: isDragging && draggedProductId === product.id ? 'grabbing' : 'grab',
          userSelect: 'none'
        }}
        className={dragOverProductIds.has(product.id) ? 'drag-over-cell' : ''}
      >
        {validationErrors?.orderQuantity && (
          <div className="text-danger small mb-1" style={{ fontSize: '0.75rem' }}>
            Обязательное поле
          </div>
        )}
        <input
          key={`orderQuantity-${product.id}-${data.orderQuantity}`}
          type="text"
          defaultValue={data.orderQuantity}
          onBlur={(e) => {
            const rawValue = e.target.value.trim();
            if (rawValue) {
              const roundedValue = roundOrderQuantity(rawValue, data.requiredPrice);
              e.target.value = roundedValue; // Обновляем значение в инпуте
              updateProductData(product.id, { orderQuantity: roundedValue }, true);
            } else {
              updateProductData(product.id, { orderQuantity: rawValue }, true);
            }
          }}
          onFocus={(e) => e.target.select()}
          placeholder="Введите количество"
          className={`form-control form-control-sm ${validationErrors?.orderQuantity ? 'is-invalid' : ''}`}
          autoComplete="off"
          spellCheck="false"
          style={validationErrors?.orderQuantity ? { borderColor: '#dc3545' } : {}}
          onClick={(e) => e.stopPropagation()}
          disabled={isBlocked}
          onMouseDown={(e) => {
            // Не останавливаем propagation, чтобы drag работал на td
          }}
          onDragStart={(e) => {
            // Предотвращаем drag самого input, чтобы drag работал на td
            e.stopPropagation();
            e.preventDefault();
          }}
        />
        <div
          className="text-muted small"
          style={{
            position: 'absolute',
            top: '71%',
            left: '0',
            right: '0',
            cursor: 'pointer',
            padding: '2px 6px',
            fontSize: '11px',
            zIndex: 10,
            pointerEvents: 'auto'
          }}
          onClick={(e) => {
            if (product.stat_sales !== undefined) {
              // Находим инпут и устанавливаем значение с округлением
              const input = e.currentTarget.previousElementSibling as HTMLInputElement;
              if (input) {
                const roundedValue = roundOrderQuantity(product.stat_sales, data.requiredPrice);
                input.value = roundedValue;
                // Вызываем blur чтобы сохранить значение
                input.focus();
                input.blur();
              }
            }
          }}
          title="Нажмите для заполнения количества продажами"
        >
          продажи: {product.stat_sales || 0}
        </div>
      </td>
      <td
        draggable={true}
        onDragStart={(e) => {
          onDragStart(e, product.id, 'packageLength', data.packageLength || 0);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDragOver(e, product.id, 'packageLength');
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDrop(e, product.id, 'packageLength');
        }}
        onDragEnd={onDragEnd}
        style={{
          cursor: isDragging && draggedProductId === product.id ? 'grabbing' : 'grab',
          userSelect: 'none'
        }}
        className={dragOverProductIds.has(product.id) ? 'drag-over-cell' : ''}
      >
        {validationErrors?.packageLength && (
          <div className="text-danger small mb-1" style={{ fontSize: '0.75rem' }}>
            Укажите значение больше 0
          </div>
        )}
        <input
          key={`packageLength-${product.id}-${data.packageLength}`}
          type="number"
          defaultValue={data.packageLength}
          onBlur={(e) => updateProductData(product.id, { packageLength: parseFloat(e.target.value) || 0 }, true)}
          onFocus={(e) => e.target.select()}
          min="0"
          step="0.1"
          className={`form-control form-control-sm ${validationErrors?.packageLength ? 'is-invalid' : ''}`}
          autoComplete="off"
          style={validationErrors?.packageLength ? { borderColor: '#dc3545' } : {}}
          onClick={(e) => e.stopPropagation()}
          disabled={isBlocked}
          onMouseDown={(e) => {
            // Не останавливаем propagation, чтобы drag работал на td
          }}
          onDragStart={(e) => {
            // Предотвращаем drag самого input, чтобы drag работал на td
            e.stopPropagation();
            e.preventDefault();
          }}
        />
      </td>
      <td
        draggable={true}
        onDragStart={(e) => {
          onDragStart(e, product.id, 'packageWidth', data.packageWidth || 0);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDragOver(e, product.id, 'packageWidth');
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDrop(e, product.id, 'packageWidth');
        }}
        onDragEnd={onDragEnd}
        style={{
          cursor: isDragging && draggedProductId === product.id ? 'grabbing' : 'grab',
          userSelect: 'none'
        }}
        className={dragOverProductIds.has(product.id) ? 'drag-over-cell' : ''}
      >
        {validationErrors?.packageWidth && (
          <div className="text-danger small mb-1" style={{ fontSize: '0.75rem' }}>
            Укажите значение больше 0
          </div>
        )}
        <input
          key={`packageWidth-${product.id}-${data.packageWidth}`}
          type="number"
          defaultValue={data.packageWidth}
          onBlur={(e) => updateProductData(product.id, { packageWidth: parseFloat(e.target.value) || 0 }, true)}
          onFocus={(e) => e.target.select()}
          min="0"
          step="0.1"
          className={`form-control form-control-sm ${validationErrors?.packageWidth ? 'is-invalid' : ''}`}
          autoComplete="off"
          style={validationErrors?.packageWidth ? { borderColor: '#dc3545' } : {}}
          onClick={(e) => e.stopPropagation()}
          disabled={isBlocked}
          onMouseDown={(e) => {
            // Не останавливаем propagation, чтобы drag работал на td
          }}
          onDragStart={(e) => {
            // Предотвращаем drag самого input, чтобы drag работал на td
            e.stopPropagation();
            e.preventDefault();
          }}
        />
      </td>
      <td
        draggable={true}
        onDragStart={(e) => {
          onDragStart(e, product.id, 'packageHeight', data.packageHeight || 0);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDragOver(e, product.id, 'packageHeight');
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDrop(e, product.id, 'packageHeight');
        }}
        onDragEnd={onDragEnd}
        style={{
          cursor: isDragging && draggedProductId === product.id ? 'grabbing' : 'grab',
          userSelect: 'none'
        }}
        className={dragOverProductIds.has(product.id) ? 'drag-over-cell' : ''}
      >
        {validationErrors?.packageHeight && (
          <div className="text-danger small mb-1" style={{ fontSize: '0.75rem' }}>
            Укажите значение больше 0
          </div>
        )}
        <input
          key={`packageHeight-${product.id}-${data.packageHeight}`}
          type="number"
          defaultValue={data.packageHeight}
          onBlur={(e) => updateProductData(product.id, { packageHeight: parseFloat(e.target.value) || 0 }, true)}
          onFocus={(e) => e.target.select()}
          min="0"
          step="0.1"
          className={`form-control form-control-sm ${validationErrors?.packageHeight ? 'is-invalid' : ''}`}
          autoComplete="off"
          style={validationErrors?.packageHeight ? { borderColor: '#dc3545' } : {}}
          onClick={(e) => e.stopPropagation()}
          disabled={isBlocked}
          onMouseDown={(e) => {
            // Не останавливаем propagation, чтобы drag работал на td
          }}
          onDragStart={(e) => {
            // Предотвращаем drag самого input, чтобы drag работал на td
            e.stopPropagation();
            e.preventDefault();
          }}
        />
      </td>
      <td>
        {validationErrors?.purchasePercent && (
          <div className="text-danger small mb-1" style={{ fontSize: '0.75rem' }}>
            Нельзя 100%
          </div>
        )}
        <div className="d-flex align-items-center">
          <input
            type="number"
            defaultValue={data.purchasePercent}
            key={`purchasePercent-${product.id}-${data.purchasePercent}`}
            onBlur={(e) => {
              const next = parseFloat(e.target.value);
              const pct = Number.isFinite(next) ? Math.min(100, Math.max(0, next)) : 0;
              updateProductData(product.id, { purchasePercent: pct }, true);
            }}
            onFocus={(e) => e.target.select()}
            min={0}
            max={100}
            step={0.01}
            className={`form-control form-control-sm ${validationErrors?.purchasePercent ? 'is-invalid' : ''}`}
            style={{
              minWidth: '80px',
              textAlign: 'center',
              ...(validationErrors?.purchasePercent ? { borderColor: '#dc3545' } : {}),
            }}
            onClick={(e) => e.stopPropagation()}
            disabled={isBlocked}
          />
          <span className="ms-1">%</span>
        </div>
      </td>
      {/* <td>
        <input
          type="checkbox"
          defaultChecked={data.isStack}
          onChange={(e) => updateProductData(product.id, { isStack: e.target.checked })}
        />
      </td> */}
      <td>
        {calculatedCosts ? formatCurrency(calculatedCosts.costPrice) : '-'}
      </td>
      <td>
        {calculatedCosts && typeof calculatedCosts.yuanPrice === 'number' && !isNaN(calculatedCosts.yuanPrice) 
          ? `${calculatedCosts.yuanPrice.toFixed(2)} ¥` 
          : '-'}
      </td>
      <td>
        <div className="d-flex gap-1">
        <Button
          variant={product.new_product && product.target_price_cny ? 'danger' : 'outline-secondary'}
          size="sm"
          title={
            !(product.new_product && product.target_price_cny) &&
            (isZeroPackageDimension(data.packageLength) || isZeroPackageDimension(data.packageWidth) || isZeroPackageDimension(data.packageHeight))
              ? 'Нельзя подтвердить: длина, ширина и высота должны быть больше 0'
              : undefined
          }
          onClick={(e) => {
            e.stopPropagation();
            const confirmed = !(product.new_product && product.target_price_cny);
            onConfirmNewProduct(product.id, confirmed);
          }}
            disabled={isUpdating || (isBlocked && !(product.new_product && product.target_price_cny))}
        >
          {isUpdating ? (
            <Spinner size="sm" animation="border" />
          ) : product.new_product && product.target_price_cny ? (
            <IconX size={16} />
          ) : (
            <IconCheck size={16} />
          )}
        </Button>
          <Button
            variant={product.stop_list ? "warning" : "danger"}
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onAddToStopList(product.id);
            }}
            disabled={isUpdating || Boolean(isBlocked && product.stop_list !== true)}
            title={product.stop_list ? "Убрать из стоплиста" : "Добавить в стоплист"}
          >
            <IconStop size={16} />
          </Button>
        </div>
      </td>
    </tr>
  );
});

const NewProducts: React.FC = () => {
  const dispatch = useAppDispatch();
  const { products, loading, error, filters, pagination, filterOptions } = useAppSelector((state) => state.products);
  const { user } = useAppSelector((state) => state.auth);
  const { statuses, loading: statusesLoading } = useAppSelector((state) => state.statuses);
  const { similarProducts, loading: similarProductsLoading, error: similarProductsError } = useAppSelector((state) => state.similarProducts);
  const { constants } = useAppSelector((state) => state.constants);
  const { certificates } = useAppSelector((state) => state.certificates);

  const [productData, setProductData] = useState<Record<string, ProductData>>({});
  const [changingStatus, setChangingStatus] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(filters.search);
  const [excludePhraseInput, setExcludePhraseInput] = useState(filters.exclude_phrase || '');
  const [updatingProducts, setUpdatingProducts] = useState<Set<string>>(new Set());
  const [validationErrors, setValidationErrors] = useState<Record<string, {
    orderQuantity?: boolean;
    packageLength?: boolean;
    packageWidth?: boolean;
    packageHeight?: boolean;
    requiredPrice?: boolean;
    purchasePercent?: boolean;
  }>>({});
  const [articleFieldErrors, setArticleFieldErrors] = useState<Record<string, string>>({});
  const [pageInput, setPageInput] = useState('');
  const [weekSummary, setWeekSummary] = useState<{ inWork: number; statusChanges: number; newProducts: number } | null>(null);
  const [historyModalProductId, setHistoryModalProductId] = useState<string | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [checkerModal, setCheckerModal] = useState<{ show: boolean; initialSku?: string }>(() => {
    const saved = loadCheckerPanelState();
    return { show: saved.open };
  });
  const [remapModal, setRemapModal] = useState<{
    show: boolean;
    product_id: string;
    product_label: string;
    source_code: string;
    from_subject_id: number;
    from_subject_name: string;
  } | null>(null);
  const [remapApplyAll, setRemapApplyAll] = useState(false);
  const [classicSubjects, setClassicSubjects] = useState<{ id: number; name: string }[]>([]);
  const [remapTargetId, setRemapTargetId] = useState<number | null>(null);
  const [remapSubmitting, setRemapSubmitting] = useState(false);
  const [classicSubjectsLoading, setClassicSubjectsLoading] = useState(false);

  // Состояния для drag and drop
  const [draggedProductId, setDraggedProductId] = useState<string | null>(null);
  const [draggedFieldType, setDraggedFieldType] = useState<'packageLength' | 'packageWidth' | 'packageHeight' | 'requiredPrice' | 'certificate' | 'orderQuantity' | null>(null);
  const [draggedValue, setDraggedValue] = useState<number | string | null>(null);
  const [dragOverProductIds, setDragOverProductIds] = useState<Set<string>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [changingFieldsBatch, setChangingFieldsBatch] = useState<Set<string>>(new Set());
  const [dragOverFieldTypes, setDragOverFieldTypes] = useState<Set<'packageLength' | 'packageWidth' | 'packageHeight'>>(new Set());

  // Данные для нового калькулятора (SPP / FBO)
  const [sppData, setSppData] = useState<SppRecord[]>([]);
  const [fboWbData, setFboWbData] = useState<FboWbRecord[]>([]);
  const [fboOzonData, setFboOzonData] = useState<FboOzonData>({});

  // Функция для принудительного округления количества заказа
  // При цене >= order_rounding: только целое число, без минимума и шагов. Иначе: минимум 200, от 200 до 1000 - шаг 50, после 1000 - шаг 100
  const roundOrderQuantity = useCallback((value: string | number, price?: number): string => {
    const numValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
    const orderRoundingRaw = constants.find(c => (c.name ?? '').trim() === 'order_rounding')?.value;
    const orderRounding = (orderRoundingRaw != null && !Number.isNaN(Number(orderRoundingRaw)))
      ? Number(orderRoundingRaw)
      : 8000;

    let result: string;
    if (price !== undefined && price >= orderRounding) {
      result = String(Math.round(numValue));
      console.log('[roundOrderQuantity] order_rounding:', orderRounding, 'цена:', price, 'ввод:', numValue, '→ результат:', result, '(округление и минимум отключены)');
      return result;
    }

    // Минимум 200
    if (numValue < 200) {
      result = '200';
      console.log('[roundOrderQuantity] order_rounding:', orderRounding, 'цена:', price, 'ввод:', numValue, '→ результат:', result);
      return result;
    }

    // От 200 до 1000 - округляем вверх до ближайшего кратного 50
    if (numValue < 1000) {
      result = Math.ceil(numValue / 50) * 50 + '';
      console.log('[roundOrderQuantity] order_rounding:', orderRounding, 'цена:', price, 'ввод:', numValue, '→ результат:', result);
      return result;
    }

    // После 1000 - округляем вверх до ближайшего кратного 100
    result = Math.ceil(numValue / 100) * 100 + '';
    console.log('[roundOrderQuantity] order_rounding:', orderRounding, 'цена:', price, 'ввод:', numValue, '→ результат:', result);
    return result;
  }, [constants]);

  // Функция для автоматического расчета себестоимости (старая формула или новая WB/Ozon с max)
  type CalculatedCosts = {
    costPrice: number;
    yuanPrice: number;
    target_price_cny_ozon?: number;
    target_price_rub_ozon?: number;
    target_price_cny_wb?: number;
    target_price_rub_wb?: number;
  } | null;
  const calculateProductCosts = useCallback((data: ProductData, product?: Product): CalculatedCosts => {
    if (!data.requiredPrice || data.requiredPrice <= 0 ||
        isZeroPackageDimension(data.packageLength) ||
        isZeroPackageDimension(data.packageWidth) ||
        isZeroPackageDimension(data.packageHeight)) {
      return null;
    }

    const quantity = data.orderQuantity ? parseInt(data.orderQuantity) || 100 : 100;
    const useNewFormula = hasNewFormulaConstants(constants) && sppData.length > 0 && fboWbData.length > 0;

    if (!useNewFormula) return null;

    const certId = data.certificate ? parseInt(data.certificate) : null;
    const selectedCertificate = certId ? certificates.find((cert: Certificate) => cert.id === certId) ?? null : null;

    try {
      const result = calculateNewFormula({
        requiredPrice: data.requiredPrice,
        packageLength: data.packageLength,
        packageWidth: data.packageWidth,
        packageHeight: data.packageHeight,
        subjectName: product?.subject_name ?? '',
        quantity,
        commissionFbo: getFboWb(product?.subject_name ?? '', fboWbData) ?? null,
        certificate: selectedCertificate,
        constants,
        sppData,
        fboWbData,
        fboOzonData,
        purchasePercent: data.purchasePercent ?? product?.stat_purchase_percent ?? null,
        nichePurchaseAvg: (() => {
          const sid = product?.subject_id;
          if (sid == null) return null;
          const vals = products
            .filter((p) => p.subject_id === sid && Number(p.stat_purchase_percent) > 0)
            .map((p) => Number(p.stat_purchase_percent));
          if (!vals.length) return null;
          return vals.reduce((a, b) => a + b, 0) / vals.length;
        })(),
      });
      return {
        costPrice: result.target_price_rub,
        yuanPrice: result.target_price_cny,
        target_price_cny_ozon: result.target_price_cny_ozon,
        target_price_rub_ozon: result.target_price_rub_ozon,
        target_price_cny_wb: result.target_price_cny_wb,
        target_price_rub_wb: result.target_price_rub_wb,
      };
    } catch (error) {
      console.error('Error calculating new formula costs:', error);
      return null;
    }
  }, [constants, certificates, sppData, fboWbData, fboOzonData, products]);

  // Refs для debounce таймеров
  const saveTimersRef = useRef<Record<string, NodeJS.Timeout>>({});
  // Ref для отслеживания изменений, которые нужно сохранить
  const pendingSavesRef = useRef<Record<string, Partial<ProductData>>>({});
  const inflightSavesRef = useRef<Record<string, Promise<void>>>({});
  const productDataRef = useRef(productData);
  productDataRef.current = productData;

  // Функция для сохранения данных товара в БД
  const saveProductToDB = useCallback(async (productId: string, updates: Partial<ProductData>) => {
    const clearArticleError = () => {
      setArticleFieldErrors((prev) => {
        if (!(productId in prev)) return prev;
        const next = { ...prev };
        delete next[productId];
        return next;
      });
    };

    try {
      const updatePayload: any = {};

      if (updates.orderQuantity !== undefined) {
        updatePayload.order_quantity = updates.orderQuantity;
      }
      if (updates.certificate !== undefined) {
        updatePayload.cert_id = updates.certificate === '' ? null : parseInt(updates.certificate);
      }
      if (updates.requiredPrice !== undefined) {
        updatePayload.required_price = updates.requiredPrice;
      }
      if (updates.packageLength !== undefined) {
        updatePayload.package_length = updates.packageLength;
      }
      if (updates.packageWidth !== undefined) {
        updatePayload.package_width = updates.packageWidth;
      }
      if (updates.packageHeight !== undefined) {
        updatePayload.package_height = updates.packageHeight;
      }
      if (updates.article !== undefined) {
        updatePayload.article = updates.article;
      }
      if (updates.name !== undefined) {
        updatePayload.name = updates.name;
      }
      if (updates.purchasePercent !== undefined) {
        updatePayload.purchase_percent = updates.purchasePercent;
      }

      if (updates.article !== undefined) {
        const product = products.find((p) => p.id === productId);
        const persisted = String(product?.article ?? product?.product_article ?? '').trim();
        const nextArt = String(updates.article ?? '').trim();
        if (nextArt === persisted) {
          clearArticleError();
          return;
        }
      }

      if (Object.keys(updatePayload).length > 0) {
        await dispatch(updateProduct({ productId, data: updatePayload })).unwrap();
        if (updates.article !== undefined) clearArticleError();
      }
    } catch (error: unknown) {
      console.error('Failed to save product data:', error);
      const e = error as { message?: string; payload?: unknown };
      const msg =
        (typeof e?.payload === 'string' ? e.payload : null) ||
        (typeof e?.message === 'string' ? e.message : null) ||
        (typeof error === 'string' ? error : null) ||
        'Не удалось сохранить данные товара';
      if (updates.article !== undefined) {
        setArticleFieldErrors((prev) => ({ ...prev, [productId]: msg }));
      }
      toast.error(msg);
    }
  }, [dispatch, products]);

  // Автоматический пересчет себестоимости при изменении данных
  const updateProductDataWithCalculation = useCallback((productId: string, updates: Partial<ProductData>, saveImmediately = false) => {
    // Обновляем state (без вызова saveProductToDB внутри)
    setProductData(prev => {
      const product = products.find(p => p.id === productId);
      const currentData = prev[productId] || {
        id: productId,
        noveltyArticle: product?.article || product?.product_article || '',
        article: product?.article || product?.product_article || '',
        name: product?.name || '',
        requiredPrice: (product as any)?.required_price || product?.average_price || 0,
        certificate: '',
        orderQuantity: '',
        packageLength: 0,
        packageWidth: 0,
        packageHeight: 0,
        purchasePercent: 0,
        isStack: false,
        costPrice: '',
        totalPriceYuan: null,
        calculatedCostPrice: undefined,
        calculatedYuanPrice: undefined,
        manualYuanPrice: product?.target_price_cny !== null && product?.target_price_cny !== undefined ? product.target_price_cny : null
      };

      const newData = { ...currentData, ...updates };

      // Выполняем расчет если все необходимые поля заполнены (передаем product для subject_name в новой формуле)
      const calculatedCosts = calculateProductCosts(newData, product ?? undefined);

      return {
        ...prev,
        [productId]: {
          ...newData,
          calculatedCostPrice: calculatedCosts?.costPrice,
          calculatedYuanPrice: calculatedCosts?.yuanPrice
          // manualYuanPrice сохраняется, не перезаписывается
        }
      };
    });

    if (updates.purchasePercent !== undefined && !isPlaceholderPurchasePercent(updates.purchasePercent)) {
      setValidationErrors(prev => {
        if (!prev[productId]?.purchasePercent) return prev;
        const nextRow = { ...prev[productId] };
        delete nextRow.purchasePercent;
        return { ...prev, [productId]: nextRow };
      });
    }

    // Логика сохранения вынесена из setProductData
      if (saveImmediately) {
        // Очищаем предыдущий таймер если есть
        if (saveTimersRef.current[productId]) {
          clearTimeout(saveTimersRef.current[productId]);
          delete saveTimersRef.current[productId];
        }
        // % выкупа держим в pending до конца запроса: подтверждение обновляет список
        // и иначе подставляет устаревшие 100% из redux поверх ручной правки.
        const keepPct = updates.purchasePercent;
        if (keepPct !== undefined) {
          pendingSavesRef.current[productId] = {
            ...pendingSavesRef.current[productId],
            purchasePercent: keepPct,
          };
        }
        const job = saveProductToDB(productId, updates).finally(() => {
          if (keepPct !== undefined) {
            const pending = pendingSavesRef.current[productId];
            if (pending && pending.purchasePercent === keepPct) {
              delete pending.purchasePercent;
              if (Object.keys(pending).length === 0) delete pendingSavesRef.current[productId];
            }
          }
          if (inflightSavesRef.current[productId] === job) delete inflightSavesRef.current[productId];
        });
        if (keepPct !== undefined) inflightSavesRef.current[productId] = job;
      } else {
      // Объединяем с существующими pending updates
      pendingSavesRef.current[productId] = {
        ...pendingSavesRef.current[productId],
        ...updates
      };
      // Очищаем предыдущий таймер если есть
        if (saveTimersRef.current[productId]) {
          clearTimeout(saveTimersRef.current[productId]);
        }
      // Устанавливаем новый таймер для debounce
        saveTimersRef.current[productId] = setTimeout(() => {
        const pendingUpdates = pendingSavesRef.current[productId];
        if (pendingUpdates) {
          saveProductToDB(productId, pendingUpdates);
          delete pendingSavesRef.current[productId];
        }
          delete saveTimersRef.current[productId];
        }, 300);
      }
  }, [calculateProductCosts, saveProductToDB, products]);

  // Автоматический пересчет всех товаров при изменении констант или сертификатов
  useEffect(() => {
    if (constants.length > 0 && certificates.length > 0) {
      setProductData(prev => {
        const newData = { ...prev };
        let hasChanges = false;

        Object.keys(newData).forEach(productId => {
          const data = newData[productId];
          const product = products.find(p => p.id === productId);
          const calculatedCosts = calculateProductCosts(data, product ?? undefined);

          // Обновляем calculatedCostPrice и calculatedYuanPrice, даже если они undefined
          if (calculatedCosts) {
            if (data.calculatedCostPrice !== calculatedCosts.costPrice ||
                data.calculatedYuanPrice !== calculatedCosts.yuanPrice) {
            newData[productId] = {
              ...data,
              calculatedCostPrice: calculatedCosts.costPrice,
              calculatedYuanPrice: calculatedCosts.yuanPrice
                // manualYuanPrice сохраняется, не перезаписывается
              };
              hasChanges = true;
            }
          } else if (data.calculatedCostPrice !== undefined || data.calculatedYuanPrice !== undefined) {
            // Если расчеты не выполняются, но значения были установлены, сбрасываем их
            newData[productId] = {
              ...data,
              calculatedCostPrice: undefined,
              calculatedYuanPrice: undefined
              // manualYuanPrice сохраняется, не перезаписывается
            };
            hasChanges = true;
          }
        });

        return hasChanges ? newData : prev;
      });
    }
  }, [constants, certificates, calculateProductCosts]);

  const [similarProductsModal, setSimilarProductsModal] = useState<{
    show: boolean;
    baseProduct: Product | null;
  }>({
    show: false,
    baseProduct: null
  });

  const [productInfoModal, setProductInfoModal] = useState<{
    show: boolean;
    productId: string;
    imageUrl: string;
  }>({ show: false, productId: '', imageUrl: '' });

  // Откладываем тяжелые вычисления
  const deferredProducts = useDeferredValue(products);
  const deferredProductData = useDeferredValue(productData);

  const [tooltip, setTooltip] = useState<{
    show: boolean;
    product: Product | null;
    position: { x: number; y: number };
  }>({
    show: false,
    product: null,
    position: { x: 0, y: 0 }
  });

  const [imageTooltip, setImageTooltip] = useState<{
    show: boolean;
    imageUrl: string;
    alt: string;
    position: { x: number; y: number };
  }>({
    show: false,
    imageUrl: '',
    alt: '',
    position: { x: 0, y: 0 }
  });

  const tableRef = useRef<HTMLTableElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [headerWidths, setHeaderWidths] = useState<number[]>([]);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Функция для получения цвета статуса
  const getStatusColor = (statusName: string) => {
    const colorMap: { [key: string]: string } = {
      'В процессе': 'status-white',
      'Расчет ЮЭ': 'status-green',
      'Уже продаем': 'status-gray',
      'Слабый потенциал': 'status-red',
      'Не соответствует ассортиментной матрице': 'status-purple',
      'Сезонность': 'status-lightblue',
      'Доп вариация': 'status-teal',
      'Требуется дополнительная консультация': 'status-yellow',
      'Электротовары': 'status-red'
    };
    return colorMap[statusName] || 'status-white';
  };

  useEffect(() => {
    dispatch(fetchStatuses());
    dispatch(fetchConstants());
    dispatch(fetchCertificates());
    dispatch(fetchFilterOptions());
  }, [dispatch]);

  // Загрузка данных для нового калькулятора (SPP / FBO), когда есть константы new_formula
  useEffect(() => {
    if (!hasNewFormulaConstants(constants)) return;
    let cancelled = false;
    Promise.all([
      api.getSppMarketplace(),
      api.getFboWb(),
      api.getFboOzon(),
    ]).then(([sppRes, fboWbRes, fboOzRes]) => {
      if (cancelled) return;
      if (sppRes?.success && sppRes?.data) setSppData(sppRes.data);
      if (fboWbRes?.success && fboWbRes?.data) setFboWbData(fboWbRes.data);
      if (fboOzRes?.success && fboOzRes?.data) setFboOzonData(fboOzRes.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [constants]);

  // Фильтрация такая же как в Products.tsx - только через user_subjects + статус "Расчет ЮЭ"

  // Автоматическая загрузка товаров при изменении фильтров или пагинации
  useEffect(() => {
    dispatch(fetchNewProducts());
  }, [dispatch, filters, pagination.page, pagination.limit]);

  const loadWeekSummary = useCallback(() => {
    const { dateFrom, dateTo } = getWeekRangeMondayToToday();
    api.getWeeklyStatistics(dateFrom, dateTo).then((data) => {
      if (data.summary) {
        setWeekSummary({
          inWork: data.summary.inWork ?? 0,
          statusChanges: data.summary.statusChanges ?? 0,
          newProducts: data.summary.newProducts ?? 0
        });
      }
    }).catch(() => setWeekSummary(null));
  }, []);

  useEffect(() => {
    loadWeekSummary();
  }, [loadWeekSummary]);

  // Cleanup таймеров при размонтировании
  useEffect(() => {
    return () => {
      Object.values(saveTimersRef.current).forEach(timer => clearTimeout(timer));
      saveTimersRef.current = {};
    };
  }, []);

  // Временно отключаем sticky headers для проверки производительности
  // Sticky headers logic
  // useEffect(() => {
  //   const handleScroll = () => {
  //     if (!tableRef.current) return;

  //     const table = tableRef.current;
  //     const tableRect = table.getBoundingClientRect();
  //     const headerRow = table.querySelector('thead tr');

  //     if (!headerRow) return;

  //     // Check if table header should be sticky
  //     const shouldBeSticky = tableRect.top <= 0 && tableRect.bottom > 0;
  //     setIsSticky(shouldBeSticky);

  //     // Store header widths when first becoming sticky
  //     if (shouldBeSticky && headerWidths.length === 0) {
  //       const widths = Array.from(headerRow.children).map(th =>
  //         (th as HTMLElement).offsetWidth
  //       );
  //       setHeaderWidths(widths);
  //     }
  //   };

  //   window.addEventListener('scroll', handleScroll);
  //   handleScroll(); // Initial check

  //   return () => {
  //     window.removeEventListener('scroll', handleScroll);
  //     // Очищаем таймер поиска при размонтировании
  //     if (searchTimeoutRef.current) {
  //       clearTimeout(searchTimeoutRef.current);
  //     }
  //   };
  // }, [headerWidths.length]);

  // Теперь сервер возвращает уже отфильтрованные и отсортированные товары
  // Клиентская пагинация не нужна, так как сервер возвращает только текущую страницу
  const paginatedProducts = deferredProducts;

  const formatCurrency = (amount: number) => {
    if (!amount) return '0 ₽';
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  const formatSubjectName = (subjectName: string) => {
    if (!subjectName) return '-';
    const slashIndex = subjectName.indexOf('/');
    return slashIndex !== -1 ? subjectName.substring(slashIndex + 1) : subjectName;
  };

  // Функция для получения данных товара
  // Инициализация данных для товаров при получении новых товаров
  useEffect(() => {
    setProductData(prev => {
      const newData = { ...prev };
      let hasChanges = false;

      products.forEach(product => {
        if (!newData[product.id]) {
          hasChanges = true;
          newData[product.id] = {
            id: product.id,
            noveltyArticle: product.article || product.product_article || '', // Артикул берется из базы данных
            article: product.article || product.product_article || '', // Редактируемый артикул
            name: product.name || '', // Редактируемое название
            requiredPrice: (product as any).required_price || product.average_price || 0,
            certificate: product.cert_id ? product.cert_id.toString() : '', // Используем ID сертификата
            orderQuantity: product.order_quantity ? product.order_quantity.toString() : '', // Загружаем из БД
            packageLength: product.package_length || 0,
            packageWidth: product.package_width || 0,
            packageHeight: product.package_height || 0,
            purchasePercent: product.stat_purchase_percent || 0,
            isStack: false,
            costPrice: '',
            totalPriceYuan: null,
            calculatedCostPrice: undefined,
            calculatedYuanPrice: undefined,
            manualYuanPrice: product.target_price_cny !== null && product.target_price_cny !== undefined ? product.target_price_cny : null
          };
        } else if (pendingSavesRef.current[product.id]?.purchasePercent === undefined) {
          const serverPct = Number(product.stat_purchase_percent);
          const localPct = Number(newData[product.id].purchasePercent);
          if (Number.isFinite(serverPct) && serverPct !== localPct) {
            hasChanges = true;
            newData[product.id] = {
              ...newData[product.id],
              purchasePercent: serverPct,
            };
          }
        }
      });

      // После инициализации выполняем расчеты для новых товаров
      if (hasChanges && (constants.length > 0 || sppData.length > 0)) {
        Object.keys(newData).forEach(productId => {
          const data = newData[productId];
          const product = products.find(p => p.id === productId);
          const calculatedCosts = calculateProductCosts(data, product ?? undefined);
          if (calculatedCosts) {
            newData[productId] = {
              ...data,
              calculatedCostPrice: calculatedCosts.costPrice,
              calculatedYuanPrice: calculatedCosts.yuanPrice
            };
          }
        });
      }

      return hasChanges ? newData : prev;
    });
  }, [products, constants, certificates, sppData, calculateProductCosts]);

  const getProductData = useCallback((productId: string): ProductData => {
    const product = products.find(p => p.id === productId);
    return productData[productId] || {
      id: productId,
      noveltyArticle: product?.article || product?.product_article || '', // Артикул берется из товара
      article: product?.article || product?.product_article || '', // Редактируемый артикул
      name: product?.name || '', // Редактируемое название
      requiredPrice: 0,
      certificate: '',
      orderQuantity: '',
      packageLength: 0,
      packageWidth: 0,
      packageHeight: 0,
      purchasePercent: 0,
      isStack: false,
      costPrice: '',
      totalPriceYuan: null,
      calculatedCostPrice: undefined,
      calculatedYuanPrice: undefined,
      manualYuanPrice: product?.target_price_cny !== null && product?.target_price_cny !== undefined ? product.target_price_cny : null
    };
  }, [productData, products]);


  // Функция для конвертации рублей в юани (используем фиксированный курс для демонстрации)
  // В реальном приложении нужно использовать надежный API конвертации валют
  const convertRubToYuan = (rubAmount: number): number | null => {
    try {
      // Для демонстрации используем примерный курс RUB to CNY
      // В реальном приложении использовать API вроде: https://api.exchangerate-api.com/v4/latest/RUB
      // или https://www.cbr-xml-daily.ru/daily_json.js для курса ЦБ РФ

      // Примерный курс на 2025 год (RUB to CNY ≈ 0.085)
      const RUB_TO_CNY_RATE = 0.085;

      return rubAmount * RUB_TO_CNY_RATE;
    } catch (error) {
      console.error('Failed to convert currency:', error);
      return null;
    }
  };


  // Проверка, заполнены ли все поля для подсветки
  // Артикул теперь берется из базы данных (product.article или product.product_article)
  const isRowComplete = useCallback((productId: string): boolean => {
    const product = products.find(p => p.id === productId);
    const data = getProductData(productId);
    return !!(
      (product?.article || product?.product_article) && // Артикул должен быть сгенерирован
      data.orderQuantity &&
      data.packageLength > 0 &&
      data.packageWidth > 0 &&
      data.packageHeight > 0 &&
      data.calculatedCostPrice !== undefined &&
      data.calculatedYuanPrice !== undefined
    );
  }, [getProductData, products]);

  // Обработчики для tooltip
  const handleProductNameMouseEnter = useCallback((product: Product, event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      show: true,
      product,
      position: {
        x: rect.left + rect.width / 2,
        y: rect.top
      }
    });
  }, []);

  const handleProductNameMouseLeave = useCallback(() => {
    setTooltip({
      show: false,
      product: null,
      position: { x: 0, y: 0 }
    });
  }, []);

  // Обработчики для увеличенного изображения
  const handleImageMouseEnter = useCallback((imageUrl: string, alt: string, event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setImageTooltip({
      show: true,
      imageUrl,
      alt,
      position: {
        x: rect.left + rect.width / 2,
        y: rect.top
      }
    });
  }, []);

  const handleImageMouseLeave = useCallback(() => {
    setImageTooltip({
      show: false,
      imageUrl: '',
      alt: '',
      position: { x: 0, y: 0 }
    });
  }, []);

  // Обработчик клика на изображение для открытия модального окна с информацией
  const handleImageClick = useCallback((product: Product, e: React.MouseEvent) => {
    e.stopPropagation();
    if (product.thumb_url) {
      setProductInfoModal({
        show: true,
        productId: product.id,
        imageUrl: product.thumb_url
      });
    }
  }, []);

  const handleStatusChange = useCallback(async (productId: string, statusId: string) => {
    setChangingStatus(productId);
    try {
      await dispatch(changeProductStatus({ productId, statusId })).unwrap();
      setWeekSummary((prev) => prev ? { ...prev, statusChanges: prev.statusChanges + 1 } : prev);
      loadWeekSummary();
    } catch (error) {
      console.error('Failed to change product status:', error);
    } finally {
      setChangingStatus(null);
    }
  }, [dispatch, loadWeekSummary]);

  const handleSimilarProductsClick = useCallback(async (product: Product, event: React.MouseEvent) => {
    event.stopPropagation();
    setSimilarProductsModal({
      show: true,
      baseProduct: product
    });
    dispatch(fetchSimilarProducts({ productId: product.id, limit: 20 }));
  }, [dispatch]);

  const handleSimilarProductsModalClose = useCallback(() => {
    setSimilarProductsModal({
      show: false,
      baseProduct: null
    });
    dispatch(clearSimilarProducts());
  }, [dispatch]);

  const handleOpenRemapSubject = useCallback(async (product: Product) => {
    const source_code = (product.source_code || 'wb').trim();
    const label = (product.article || product.name || product.id).trim();
    setRemapApplyAll(false);
    setRemapModal({
      show: true,
      product_id: String(product.id),
      product_label: label,
      source_code,
      from_subject_id: Number(product.subject_id),
      from_subject_name: product.subject_name || String(product.subject_id),
    });
    setRemapTargetId(null);
    if (classicSubjects.length > 0) return;
    setClassicSubjectsLoading(true);
    try {
      const res = await api.getAllSubjects();
      const list = (res.data?.data || res.data?.subjects || res.data?.items || []) as {
        id: number;
        name: string;
      }[];
      setClassicSubjects(
        list
          .filter((s) => Number(s.id) < SUBJECT_SURROGATE_MIN)
          .map((s) => ({ id: Number(s.id), name: s.name }))
      );
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.message || 'Не удалось загрузить предметы WB');
    } finally {
      setClassicSubjectsLoading(false);
    }
  }, [classicSubjects.length]);

  const handleConfirmRemapSubject = useCallback(async () => {
    if (!remapModal || remapTargetId == null) {
      toast.error('Выберите целевой предмет WB');
      return;
    }
    const target = classicSubjects.find((s) => s.id === remapTargetId);
    const targetLabel = `«${target?.name || remapTargetId}» (${remapTargetId})`;
    const ok = window.confirm(
      remapApplyAll
        ? `Заменить нишу у всех товаров источника «${remapModal.source_code}» ` +
            `с subject «${remapModal.from_subject_name}» (${remapModal.from_subject_id}) ` +
            `на ${targetLabel}?`
        : `Заменить нишу только у товара «${remapModal.product_label}» ` +
            `(id ${remapModal.product_id}) на ${targetLabel}?`
    );
    if (!ok) return;

    setRemapSubmitting(true);
    try {
      const res = await api.remapProductsSubject({
        source_code: remapModal.source_code,
        from_subject_id: remapModal.from_subject_id,
        to_subject_id: remapTargetId,
        apply_all: remapApplyAll,
        product_id: remapApplyAll ? undefined : remapModal.product_id,
      });
      const pct = res?.purchase_percent;
      toast.success(
        pct != null
          ? `Обновлено товаров: ${res?.updated ?? 0}. % выкупа: ${pct}`
          : `Обновлено товаров: ${res?.updated ?? 0}`
      );
      setRemapModal(null);
      setRemapTargetId(null);
      dispatch(fetchNewProducts());
      dispatch(fetchFilterOptions());
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.message || 'Не удалось заменить нишу');
    } finally {
      setRemapSubmitting(false);
    }
  }, [remapModal, remapTargetId, remapApplyAll, classicSubjects, dispatch]);

  const handleFilterChange = (field: string, value: string) => {
    dispatch(setFilters({ [field]: value }));
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    // Debounced search
    const timeoutId = setTimeout(() => {
      dispatch(setFilters({ search: value }));
    }, 300);
    return () => clearTimeout(timeoutId);
  };

  const handleExcludePhraseChange = (value: string) => {
    setExcludePhraseInput(value);
    // Debounced exclude phrase
    const timeoutId = setTimeout(() => {
      dispatch(setFilters({ exclude_phrase: value }));
    }, 300);
    return () => clearTimeout(timeoutId);
  };

  const handlePageChange = (page: number) => {
    dispatch(setPagination({ page }));
  };

  const handlePageJump = () => {
    const pageNum = parseInt(pageInput);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= pagination.totalPages) {
      handlePageChange(pageNum);
      setPageInput('');
    } else {
      alert(`Введите номер страницы от 1 до ${pagination.totalPages}`);
    }
  };

  // Обработчик добавления в стоплист
  const handleAddToStopList = useCallback(async (productId: string) => {
    setUpdatingProducts(prev => new Set(prev).add(productId));
    try {
      await dispatch(addToStopList(productId)).unwrap();
      loadWeekSummary();
    } catch (error: any) {
      console.error('Failed to add product to stop list:', error);
      alert('Ошибка: ' + (error.message || 'Не удалось добавить товар в стоплист'));
    } finally {
      setUpdatingProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  }, [dispatch, loadWeekSummary]);

  // Обработчик подтверждения нового товара
  const handleConfirmNewProduct = useCallback(async (productId: string, confirmed: boolean) => {
    const inflight = inflightSavesRef.current[productId];
    if (inflight) await inflight;
    const current = productDataRef.current[productId];
    if (confirmed) {
      const dimensionErrors = {
        packageLength: isZeroPackageDimension(current?.packageLength),
        packageWidth: isZeroPackageDimension(current?.packageWidth),
        packageHeight: isZeroPackageDimension(current?.packageHeight),
      };
      const purchasePercent = isPlaceholderPurchasePercent(current?.purchasePercent);
      if (dimensionErrors.packageLength || dimensionErrors.packageWidth || dimensionErrors.packageHeight || purchasePercent) {
        setValidationErrors(prev => ({
          ...prev,
          [productId]: { ...prev[productId], ...dimensionErrors, ...(purchasePercent ? { purchasePercent: true } : {}) },
        }));
        if (purchasePercent) {
          toast.error('Подтверждение запрещено: укажите процент выкупа, отличный от 100%');
        }
        return;
      }
    }

    setUpdatingProducts(prev => new Set(prev).add(productId));
    try {
      const data = productDataRef.current[productId];
      if (!data) {
        console.error('Product data not found for', productId);
        return;
      }

      const product = products.find(p => p.id === productId);
      const calculatedCosts = calculateProductCosts(data, product ?? undefined);
      const targetPriceCny = confirmed ? (
        data.manualYuanPrice !== null && data.manualYuanPrice !== undefined
          ? data.manualYuanPrice
          : (calculatedCosts && typeof calculatedCosts.yuanPrice === 'number' && !isNaN(calculatedCosts.yuanPrice)
              ? calculatedCosts.yuanPrice
              : null)
      ) : null;
      const targetPriceRub = calculatedCosts?.costPrice ?? null;
      const targetPriceCnyOzon = calculatedCosts?.target_price_cny_ozon ?? null;
      const targetPriceRubOzon = calculatedCosts?.target_price_rub_ozon ?? null;
      const targetPriceCnyWb = calculatedCosts?.target_price_cny_wb ?? null;
      const targetPriceRubWb = calculatedCosts?.target_price_rub_wb ?? null;

      await dispatch(confirmNewProduct({
        productId,
        confirmed,
        targetPriceCny,
        targetPriceRub,
        targetPriceCnyOzon,
        targetPriceRubOzon,
        targetPriceCnyWb,
        targetPriceRubWb,
      })).unwrap();
      setWeekSummary((prev) => prev ? { ...prev, newProducts: prev.newProducts + (confirmed ? 1 : -1) } : prev);
      loadWeekSummary();
      // При успешном сохранении очищаем ошибки валидации
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[productId];
        return newErrors;
      });
    } catch (error: any) {
      console.error('Failed to confirm new product:', error);
      
      // Обрабатываем ошибки валидации
      if (error?.validationErrors) {
        setValidationErrors(prev => ({
          ...prev,
          [productId]: error.validationErrors
        }));
        if (error.validationErrors.purchasePercent) {
          toast.error('Подтверждение запрещено: укажите процент выкупа, отличный от 100%');
        }
      }
    } finally {
      setUpdatingProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  }, [dispatch, products, calculateProductCosts, loadWeekSummary]);

  // Обработчики drag and drop для полей товара
  const handleDragStart = useCallback((e: React.DragEvent, productId: string, fieldType: 'packageLength' | 'packageWidth' | 'packageHeight' | 'requiredPrice' | 'certificate' | 'orderQuantity', value: number | string) => {
    setDraggedProductId(productId);
    setDraggedFieldType(fieldType);
    setDraggedValue(value);
    setIsDragging(true);
    setDragOverProductIds(new Set([productId]));
    // Инициализируем затронутые колонки только исходной колонкой
    if (['packageLength', 'packageWidth', 'packageHeight'].includes(fieldType)) {
      setDragOverFieldTypes(new Set([fieldType as 'packageLength' | 'packageWidth' | 'packageHeight']));
    } else {
      setDragOverFieldTypes(new Set());
    }

    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', productId);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, targetProductId: string, targetFieldType?: 'packageLength' | 'packageWidth' | 'packageHeight' | 'requiredPrice' | 'certificate' | 'orderQuantity') => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedProductId || !draggedFieldType) return;

    const sourceIndex = paginatedProducts.findIndex(p => p.id === draggedProductId);
    const targetIndex = paginatedProducts.findIndex(p => p.id === targetProductId);

    if (sourceIndex === -1 || targetIndex === -1) return;

    // Определяем все строки между начальной и конечной (включая обе)
    const startIndex = Math.min(sourceIndex, targetIndex);
    const endIndex = Math.max(sourceIndex, targetIndex);
    const affectedProductIds = paginatedProducts
      .slice(startIndex, endIndex + 1)
      .map(p => p.id);

    setDragOverProductIds(new Set(affectedProductIds));

    // Если это горизонтальное перетаскивание между колонками размеров
    if (targetFieldType && 
        ['packageLength', 'packageWidth', 'packageHeight'].includes(draggedFieldType) &&
        ['packageLength', 'packageWidth', 'packageHeight'].includes(targetFieldType)) {
      // Определяем все колонки между начальной и конечной
      const sizeFields: ('packageLength' | 'packageWidth' | 'packageHeight')[] = ['packageLength', 'packageWidth', 'packageHeight'];
      const sourceFieldIndex = sizeFields.indexOf(draggedFieldType as 'packageLength' | 'packageWidth' | 'packageHeight');
      const targetFieldIndex = sizeFields.indexOf(targetFieldType as 'packageLength' | 'packageWidth' | 'packageHeight');
      
      if (sourceFieldIndex !== -1 && targetFieldIndex !== -1) {
        const startFieldIndex = Math.min(sourceFieldIndex, targetFieldIndex);
        const endFieldIndex = Math.max(sourceFieldIndex, targetFieldIndex);
        const affectedFieldTypes = sizeFields.slice(startFieldIndex, endFieldIndex + 1);
        setDragOverFieldTypes(new Set(affectedFieldTypes));
      }
    } else {
      // Для вертикального перетаскивания или других полей - только исходная колонка
      if (['packageLength', 'packageWidth', 'packageHeight'].includes(draggedFieldType)) {
        setDragOverFieldTypes(new Set([draggedFieldType as 'packageLength' | 'packageWidth' | 'packageHeight']));
      } else {
        setDragOverFieldTypes(new Set());
      }
    }
  }, [draggedProductId, draggedFieldType, paginatedProducts]);

  const handleDragEnd = useCallback(() => {
    setDraggedProductId(null);
    setDraggedFieldType(null);
    setDraggedValue(null);
    setDragOverProductIds(new Set());
    setDragOverFieldTypes(new Set());
    setIsDragging(false);
  }, []);

  // Пакетное применение значений полей
  const handleBatchFieldChange = useCallback(async (productIds: string[], fieldType: 'packageLength' | 'packageWidth' | 'packageHeight' | 'requiredPrice' | 'certificate' | 'orderQuantity', value: number | string) => {
    const newChangingBatch = new Set(productIds);
    setChangingFieldsBatch(newChangingBatch);

    // Применяем значение ко всем затронутым товарам
    const updates: Partial<ProductData> = {};
    
    switch (fieldType) {
      case 'packageLength':
        updates.packageLength = value as number;
        break;
      case 'packageWidth':
        updates.packageWidth = value as number;
        break;
      case 'packageHeight':
        updates.packageHeight = value as number;
        break;
      case 'requiredPrice':
        updates.requiredPrice = value as number;
        break;
      case 'certificate':
        updates.certificate = value as string;
        break;
      case 'orderQuantity':
        // Округление количества применяется отдельно для каждого товара с учётом цены
        break;
    }

    // Обновляем данные для всех товаров синхронно
    setProductData(prev => {
      const newData = { ...prev };
      productIds.forEach(productId => {
        const product = products.find(p => p.id === productId);
        const currentData = prev[productId] || {
          id: productId,
          noveltyArticle: product?.article || product?.product_article || '',
          article: product?.article || product?.product_article || '',
          name: product?.name || '',
          requiredPrice: product?.average_price || 0,
          certificate: product?.cert_id ? product.cert_id.toString() : '',
          orderQuantity: product?.order_quantity ? product.order_quantity.toString() : '',
          packageLength: product?.package_length || 0,
          packageWidth: product?.package_width || 0,
          packageHeight: product?.package_height || 0,
          purchasePercent: product?.stat_purchase_percent || 0,
          isStack: false,
          costPrice: '',
          totalPriceYuan: null,
          calculatedCostPrice: undefined,
          calculatedYuanPrice: undefined
        };
        const productUpdates = { ...updates };
        if (fieldType === 'orderQuantity') {
          const price = (product as { required_price?: number })?.required_price ?? product?.average_price ?? 0;
          productUpdates.orderQuantity = roundOrderQuantity(value as string | number, price);
        }
        newData[productId] = { ...currentData, ...productUpdates };
      });
      return newData;
    });

    // Сохраняем в БД для всех товаров (для orderQuantity — своё значение у каждого товара)
    if (fieldType === 'orderQuantity') {
      await Promise.all(productIds.map(productId => {
        const product = products.find(p => p.id === productId);
        const price = (product as { required_price?: number })?.required_price ?? product?.average_price ?? 0;
        const orderQty = roundOrderQuantity(value as string | number, price);
        return saveProductToDB(productId, { ...updates, orderQuantity: orderQty });
      }));
    } else {
      await Promise.all(productIds.map(productId =>
        saveProductToDB(productId, updates)
      ));
    }

    // Небольшая задержка перед очисткой индикатора загрузки
    setTimeout(() => {
      setChangingFieldsBatch(new Set());
    }, 100);
  }, [products, roundOrderQuantity, saveProductToDB]);

  const handleDrop = useCallback(async (e: React.DragEvent, targetProductId: string, targetFieldType?: 'packageLength' | 'packageWidth' | 'packageHeight' | 'requiredPrice' | 'certificate' | 'orderQuantity') => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedProductId || !draggedFieldType || draggedValue === null) return;

    const affectedProductIds = Array.from(dragOverProductIds);
    const affectedFieldTypes = Array.from(dragOverFieldTypes);

    if (affectedProductIds.length > 0) {
      // Если это горизонтальное перетаскивание между размерами (длина/ширина/высота)
      if (targetFieldType && 
          ['packageLength', 'packageWidth', 'packageHeight'].includes(draggedFieldType) &&
          ['packageLength', 'packageWidth', 'packageHeight'].includes(targetFieldType)) {
        // Применяем значение ко всем затронутым колонкам
        await Promise.all(affectedFieldTypes.map(fieldType => 
          handleBatchFieldChange(affectedProductIds, fieldType, draggedValue)
        ));
      } else {
        // Обычное вертикальное перетаскивание - применяем к тому же типу поля
        await handleBatchFieldChange(affectedProductIds, draggedFieldType, draggedValue);
      }
    }

    handleDragEnd();
  }, [draggedProductId, draggedFieldType, draggedValue, dragOverProductIds, dragOverFieldTypes, handleBatchFieldChange, handleDragEnd]);

  // Получаем уникальные значения для фильтров
  // Предметы и страны теперь берутся из БД через filterOptions
  const uniqueSubjects = useMemo(() => {
    // Всегда используем filterOptions, если они загружены
    if (filterOptions?.subjects && filterOptions.subjects.length > 0) {
      return filterOptions.subjects;
    }
    // Fallback на данные из products только если filterOptions еще не загружены
    const subjects = products.map(p => ({ id: p.subject_id, name: p.subject_name }));
    return subjects.filter((subject, index, self) =>
      index === self.findIndex(s => s.id === subject.id)
    );
  }, [filterOptions, products]);

  const uniqueCountries = useMemo(() => {
    if (filterOptions?.countries) {
      return filterOptions.countries;
    }
    // Fallback на данные из products, если filterOptions еще не загружены
    return Array.from(new Set(products.map(p => p.country).filter(Boolean)));
  }, [filterOptions, products]);

  const sourceOptions = filterOptions?.sources || [];

  return (
    <Container fluid className="py-4">
      <h2 className="mb-4">Новые товары</h2>

      {/* Фильтры */}
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <IconFilter className="me-2" />
              Фильтры и поиск
            </h5>
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <label className="form-label">Поиск</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Поиск по названию, SKU, предмету, аналитику..."
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  autoComplete="off"
                  spellCheck="false"
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <label className="form-label">Исключить фразы</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="фраза1, фраза2"
                  value={excludePhraseInput}
                  onChange={(e) => handleExcludePhraseChange(e.target.value)}
                  autoComplete="off"
                  spellCheck="false"
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <div className="mb-3">
                <label className="form-label">Предмет</label>
                <SubjectFilterSelect
                  subjects={uniqueSubjects}
                  value={filters.subject_id}
                  onChange={(id) => handleFilterChange('subject_id', id)}
                />
              </div>
            </Col>
            <Col md={3}>
              <div className="mb-3">
                <label className="form-label">Страна</label>
                <CountryFilterSelect
                  countries={uniqueCountries}
                  value={filters.country}
                  onChange={(country) => handleFilterChange('country', country)}
                />
              </div>
            </Col>
            <Col md={3}>
              <div className="mb-3">
                <label className="form-label">Источник</label>
                <SourceFilterSelect
                  sources={sourceOptions}
                  value={filters.source_code}
                  onChange={(code) => handleFilterChange('source_code', code)}
                />
              </div>
            </Col>
            <Col md={3}>
              <div className="mb-3">
                <label className="form-label" style={{ marginBottom: '0.5rem', display: 'block' }}>Фильтры</label>
                <div className="d-flex gap-3 align-items-center" style={{ minHeight: '38px' }}>
                  <Form.Check
                    type="checkbox"
                    id="exclude-stop-list"
                    label="Скрыть из стоплиста"
                    checked={filters.exclude_stop_list}
                    onChange={(e) => {
                      dispatch(setFilters({ exclude_stop_list: e.target.checked }));
                    }}
                    className="form-check-inline"
                    style={{ margin: 0 }}
                  />
                  <Form.Check
                    type="checkbox"
                    id="exclude-confirmed"
                    label="Скрыть подтвержденные"
                    checked={filters.exclude_confirmed}
                    onChange={(e) => {
                      dispatch(setFilters({ exclude_confirmed: e.target.checked }));
                    }}
                    className="form-check-inline"
                    style={{ margin: 0 }}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Таблица товаров */}
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center flex-wrap gap-2">
              <h5 className="mb-0">
                <IconSearch className="me-2" />
                Новые товары
                {pagination.total > 0 && (
                  <Badge bg="primary" className="ms-2">
                    {pagination.total} товаров
                  </Badge>
                )}
              </h5>
              {weekSummary !== null && (
                <span className="text-muted small">
                  В работе: {weekSummary.inWork} · Кол-во проверенных товаров: {weekSummary.statusChanges} · Прошли ЮЭ: {weekSummary.newProducts}
                </span>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" size="sm" id="checker-dropdown-new-products">
                  <IconCheckSquare size={14} className="me-1" />
                  Чекер
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => { saveCheckerPanelState({ open: true }); setCheckerModal({ show: true }); }}>
                    Открыть тут
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => window.open('/products/checker', '_blank')}>
                    Открыть в новой вкладке
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => window.open('/products/checker', '_blank', 'noopener,noreferrer,width=900,height=700')}>
                    Открыть в новом окне
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              <Button variant="outline-primary" size="sm" onClick={() => dispatch(fetchNewProducts())} disabled={loading}>
                <IconRefreshCw className={loading ? 'fa-spin' : ''} />
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Загрузка...</span>
              </Spinner>
            </div>
          ) : error ? (
            <div className="text-center py-4">
              <Alert variant="warning">
                {error}
              </Alert>
            </div>
          ) : paginatedProducts.length === 0 ? (
            <div className="text-center py-4">
              <Alert variant="info">
                Товары не найдены. Попробуйте изменить параметры поиска.
              </Alert>
            </div>
          ) : (
            <>
              {/* Sticky header clone отключен для проверки производительности */}
              {/* {isSticky && headerWidths.length > 0 && (
                <div
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    backgroundColor: '#343a40',
                    borderBottom: '1px solid #495057',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <Table responsive hover variant="dark" className="table-dark" style={{ marginBottom: 0 }}>
                    <thead>
                      <tr>
                        <th style={{ width: headerWidths[0] || 'auto' }}>img</th>
                        <th style={{ width: headerWidths[1] || 'auto' }}>SKU</th>
                        <th style={{ width: headerWidths[2] || 'auto' }}>Артикул</th>
                        <th style={{ width: headerWidths[3] || 'auto' }}>Название</th>
                        <th style={{ width: headerWidths[4] || 'auto' }}>Предмет WB</th>
                        <th style={{ width: headerWidths[5] || 'auto' }}>Нужная цена на wb</th>
                        <th style={{ width: headerWidths[6] || 'auto' }}>Сертификат</th>
                        <th style={{ width: headerWidths[7] || 'auto' }}>Заказ, количество</th>
                        <th style={{ width: headerWidths[8] || 'auto' }}>Длина упак. тары, см</th>
                        <th style={{ width: headerWidths[9] || 'auto' }}>Ширина упак. тары, см</th>
                        <th style={{ width: headerWidths[10] || 'auto' }}>Высота упак. тары, см</th>
                        <th style={{ width: headerWidths[11] || 'auto' }}>%Выкуп</th>
                        <th style={{ width: headerWidths[12] || 'auto' }}>Стек</th>
                        <th style={{ width: headerWidths[13] || 'auto' }}>Себестоимость, руб.</th>
                        <th style={{ width: headerWidths[14] || 'auto' }}>Итог цены в юанях</th>
                      </tr>
                    </thead>
                  </Table>
                </div>
              )} */}

              <Table ref={tableRef} variant="dark" className="table-dark">
                <thead>
                  <tr>
                    <th>img</th>
                    <th>SKU</th>
                    <th>Артикул</th>
                    <th>Название</th>
                    <th>Предмет WB</th>
                    <th>Нужная цена на wb</th>
                    <th>Сертификат</th>
                    <th>Заказ, количество</th>
                    <th>Длина упак. тары, см</th>
                    <th>Ширина упак. тары, см</th>
                    <th>Высота упак. тары, см</th>
                    <th>%Выкуп</th>
                    {/* <th>Стек</th> */}
                    <th>Себестоимость, руб.</th>
                    <th>Итог цены в юанях</th>
                    <th>Подтвердить</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedProducts.map((product) => {
                    // Используем deferred данные для менее критичных вычислений
                    const data = deferredProductData[product.id] || productData[product.id] || {
                      id: product.id,
                      noveltyArticle: product.article || product.product_article || '', // Артикул берется из товара
                      article: product.article || product.product_article || '', // Редактируемый артикул
                      name: product.name || '', // Редактируемое название
                      requiredPrice: (product as any).required_price || product.average_price || 0,
                      certificate: product.cert_id ? product.cert_id.toString() : '', // Используем ID сертификата
                      orderQuantity: product.order_quantity ? product.order_quantity.toString() : '', // Загружаем из БД
                      packageLength: product.package_length || 0,
                      packageWidth: product.package_width || 0,
                      packageHeight: product.package_height || 0,
                      purchasePercent: product.stat_purchase_percent || 0,
                      isStack: false,
                      costPrice: '',
                      totalPriceYuan: null,
                      calculatedCostPrice: undefined,
                      calculatedYuanPrice: undefined,
                      manualYuanPrice: product.target_price_cny !== null && product.target_price_cny !== undefined ? product.target_price_cny : null
                    };
                    const isComplete = !!(
                      (product.article || product.product_article) && // Артикул должен быть сгенерирован
                      data.orderQuantity &&
                      data.packageLength > 0 &&
                      data.packageWidth > 0 &&
                      data.packageHeight > 0 &&
                      data.calculatedCostPrice !== undefined &&
                      data.calculatedYuanPrice !== undefined
                    );

                    // Рассчитываем стоимости для товара
                    const calculatedCosts = calculateProductCosts(data, product);
                    // Себестоимость рассчитывается всегда, если есть все необходимые данные
                    const costPrice = calculatedCosts?.costPrice ?? (data.calculatedCostPrice !== undefined && typeof data.calculatedCostPrice === 'number' && !isNaN(data.calculatedCostPrice) ? data.calculatedCostPrice : undefined);
                    // Итог цены в юанях:
                    // - Если new_product = true - показываем из БД (product.target_price_cny), если нет - fallback на рассчитанную
                    // - Если new_product = false - показываем рассчитанную цену
                    const yuanPrice = product.new_product === true
                      ? (product.target_price_cny !== null && product.target_price_cny !== undefined && typeof product.target_price_cny === 'number' && !isNaN(product.target_price_cny)
                          ? product.target_price_cny
                          : (data.manualYuanPrice !== null && data.manualYuanPrice !== undefined && typeof data.manualYuanPrice === 'number' && !isNaN(data.manualYuanPrice)
                              ? data.manualYuanPrice
                              : (calculatedCosts && typeof calculatedCosts.yuanPrice === 'number' && !isNaN(calculatedCosts.yuanPrice)
                                  ? calculatedCosts.yuanPrice
                                  : (data.calculatedYuanPrice !== undefined && typeof data.calculatedYuanPrice === 'number' && !isNaN(data.calculatedYuanPrice)
                                      ? data.calculatedYuanPrice
                                      : undefined))))
                      : (calculatedCosts && typeof calculatedCosts.yuanPrice === 'number' && !isNaN(calculatedCosts.yuanPrice)
                          ? calculatedCosts.yuanPrice
                          : (data.calculatedYuanPrice !== undefined && typeof data.calculatedYuanPrice === 'number' && !isNaN(data.calculatedYuanPrice)
                              ? data.calculatedYuanPrice
                              : undefined));
                    // Создаем finalCalculatedCosts: costPrice всегда, yuanPrice всегда (из БД или рассчитанная)
                    const finalCalculatedCosts = costPrice !== undefined
                      ? { 
                          costPrice: costPrice, 
                          yuanPrice: yuanPrice !== undefined && typeof yuanPrice === 'number' && !isNaN(yuanPrice) ? yuanPrice : undefined
                        }
                      : null;

                    return (
                      <ProductTableRow
                        key={product.id}
                        product={product}
                        data={data}
                                        calculatedCosts={finalCalculatedCosts}
                        isRowComplete={isComplete}
                        onImageMouseEnter={handleImageMouseEnter}
                        onImageMouseLeave={handleImageMouseLeave}
                                        onImageClick={handleImageClick}
                        onProductNameMouseEnter={handleProductNameMouseEnter}
                        onProductNameMouseLeave={handleProductNameMouseLeave}
                        onStatusChange={handleStatusChange}
                        onSimilarProductsClick={handleSimilarProductsClick}
                        updateProductData={updateProductDataWithCalculation}
                        convertRubToYuan={convertRubToYuan}
                        formatSubjectName={formatSubjectName}
                        formatCurrency={formatCurrency}
                        formatDate={formatDate}
                        certificates={certificates}
                        onConfirmNewProduct={handleConfirmNewProduct}
                                        onAddToStopList={handleAddToStopList}
                        isUpdating={updatingProducts.has(product.id)}
                        validationErrors={validationErrors[product.id]}
                        roundOrderQuantity={roundOrderQuantity}
                                        onDragStart={handleDragStart}
                                        onDragOver={handleDragOver}
                                        onDrop={handleDrop}
                                        onDragEnd={handleDragEnd}
                                        isDragging={isDragging}
                                        draggedProductId={draggedProductId}
                                        dragOverProductIds={dragOverProductIds}
                                        changingFieldsBatch={changingFieldsBatch}
                        articleError={articleFieldErrors[product.id]}
                        onClearArticleError={() => {
                          setArticleFieldErrors((prev) => {
                            if (!(product.id in prev)) return prev;
                            const next = { ...prev };
                            delete next[product.id];
                            return next;
                          });
                        }}
                        onOpenChecker={(sku) => {
                          saveCheckerPanelState({ open: true });
                          setCheckerModal({ show: true, initialSku: sku });
                        }}
                        onRemapSubject={handleOpenRemapSubject}
                      />
                    );
                  })}
                </tbody>
              </Table>

              <ProductHistoryModal
                show={showHistoryModal}
                onHide={() => { setShowHistoryModal(false); setHistoryModalProductId(null); }}
                productId={historyModalProductId || ''}
                productLabel={historyModalProductId ? `ID ${historyModalProductId}` : undefined}
              />

              {/* Пагинация */}
              {pagination.totalPages > 1 && (
                <div className="d-flex justify-content-center align-items-center mt-3">
                  <Pagination>
                    <Pagination.First
                      onClick={() => handlePageChange(1)}
                      disabled={pagination.page === 1}
                    />
                    <Pagination.Prev
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                    />

                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      const pageNum = Math.max(1, Math.min(pagination.totalPages - 4, pagination.page - 2)) + i;
                      return (
                        <Pagination.Item
                          key={pageNum}
                          active={pageNum === pagination.page}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      );
                    })}

                    <Pagination.Next
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                    />
                    <Pagination.Last
                      onClick={() => handlePageChange(pagination.totalPages)}
                      disabled={pagination.page === pagination.totalPages}
                    />
                  </Pagination>
                  <div className="d-flex align-items-center gap-2 ms-3">
                    <Form.Control
                      type="number"
                      size="sm"
                      value={pageInput}
                      onChange={(e) => setPageInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handlePageJump();
                        }
                      }}
                      placeholder="Стр."
                      style={{ maxWidth: '80px', textAlign: 'center' }}
                      min="1"
                      max={pagination.totalPages}
                    />
                    <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                      из {pagination.totalPages}
                    </span>
                    <Button size="sm" variant="outline-secondary" onClick={handlePageJump}>
                      Перейти
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Product Tooltip */}
      <ProductTooltip
        product={tooltip.product!}
        show={tooltip.show}
        position={tooltip.position}
      />

      {/* Image Tooltip */}
      <ImageTooltip
        imageUrl={imageTooltip.imageUrl}
        alt={imageTooltip.alt}
        show={imageTooltip.show}
        position={imageTooltip.position}
      />

      {/* Similar Products Modal */}
      <SimilarProductsModal
        show={similarProductsModal.show}
        onHide={handleSimilarProductsModalClose}
        similarProducts={similarProducts}
        loading={similarProductsLoading}
        error={similarProductsError}
        baseProductName={similarProductsModal.baseProduct?.name || ''}
        baseProductId={similarProductsModal.baseProduct?.id || ''}
      />

      {/* Product Info Modal */}
      <ProductInfoModal
        show={productInfoModal.show}
        productId={productInfoModal.productId}
        imageUrl={productInfoModal.imageUrl}
        onHide={() => setProductInfoModal({ show: false, productId: '', imageUrl: '' })}
      />

      <CheckerModal
        show={checkerModal.show}
        onHide={() => { saveCheckerPanelState({ open: false }); setCheckerModal({ show: false }); }}
        initialSku={checkerModal.initialSku}
      />

      <Modal
        show={Boolean(remapModal?.show)}
        onHide={() => {
          if (remapSubmitting) return;
          setRemapModal(null);
          setRemapTargetId(null);
          setRemapApplyAll(false);
        }}
        centered
      >
        <Modal.Header closeButton={!remapSubmitting}>
          <Modal.Title>Заменить нишу на WB</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {remapModal && (
            <>
              <p className="mb-2">
                Товар: <strong>{remapModal.product_label}</strong> (id {remapModal.product_id})
                <br />
                Источник: <strong>{remapModal.source_code}</strong>
                <br />
                Текущий subject:{' '}
                <strong>
                  {formatSubjectName(remapModal.from_subject_name)} ({remapModal.from_subject_id})
                </strong>
              </p>
              <Form.Check
                type="checkbox"
                id="remap-apply-all"
                className="mb-2"
                label="Применить для всех"
                checked={remapApplyAll}
                disabled={remapSubmitting}
                onChange={(e) => setRemapApplyAll(e.target.checked)}
              />
              <p className="text-muted small">
                {remapApplyAll
                  ? 'Будут обновлены все активные товары с этой парой (source_code + subject_id).'
                  : 'Будет обновлён только этот товар.'}{' '}
                Постоянный маппинг в справочнике не создаётся. Если целевая ниша не назначена
                вам в user_subjects, товар может пропасть из списка.
              </p>
              <label className="form-label">Целевой предмет WB</label>
              {classicSubjectsLoading ? (
                <div className="d-flex align-items-center gap-2 text-muted">
                  <Spinner animation="border" size="sm" />
                  Загрузка предметов…
                </div>
              ) : (
                <Select
                  options={classicSubjects.map((s) => ({
                    value: s.id,
                    label: `${formatSubjectName(s.name)} (${s.id})`,
                  }))}
                  value={
                    remapTargetId != null
                      ? {
                          value: remapTargetId,
                          label: (() => {
                            const s = classicSubjects.find((x) => x.id === remapTargetId);
                            return s
                              ? `${formatSubjectName(s.name)} (${s.id})`
                              : String(remapTargetId);
                          })(),
                        }
                      : null
                  }
                  onChange={(opt) => setRemapTargetId(opt?.value ?? null)}
                  placeholder="Выберите классический subject WB"
                  isSearchable
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 10050 }) }}
                />
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            disabled={remapSubmitting}
            onClick={() => {
              setRemapModal(null);
              setRemapTargetId(null);
              setRemapApplyAll(false);
            }}
          >
            Отмена
          </Button>
          <Button
            variant="primary"
            disabled={remapSubmitting || remapTargetId == null || classicSubjectsLoading}
            onClick={() => void handleConfirmRemapSubject()}
          >
            {remapSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Меняем…
              </>
            ) : (
              'Заменить'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default NewProducts;
